import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { DEFAULT_MIN_SIZES, DIALOG_MARGIN } from './DialogConstant';
import { makeStyles } from '@material-ui/core';
import { ResizeCallbackData } from 'react-resizable';
import { getOffset, parseTransform } from 'utils/DomUtils';
import { ControlPosition } from 'react-draggable';

export interface DialogPosition {
  left?: number;
  top?: number;
}

interface MakeStyleOptions {
  isResponsive?: boolean;
  styles?: CSSProperties;
  isFullScreen?: boolean;
  dialogPosition?: DialogPosition;
}

interface PositionOptions {
  paperElement?: HTMLDivElement;
  fixed?: ControlPosition;
  data?: ResizeCallbackData;
  windowWidth: number;
  windowHeight: number;
}

export const useStyles = makeStyles({
  dialogPaper: (props: MakeStyleOptions) => {
    if (!props.styles) return {};
    const cssProperties = { ...props.styles };
    delete cssProperties.height;
    delete cssProperties.width;
    return { cssProperties };
  },

  dialogPaperSize: (props: MakeStyleOptions) => {
    if (!props.styles) return { minWidth: DEFAULT_MIN_SIZES, minHeight: DEFAULT_MIN_SIZES };
    const { width, height } = !props.isResponsive //resizer will handle the sizing
      ? { width: 'auto', height: 'auto' }
      : { width: props.styles.width, height: props.styles.height };
    return {
      ...{
        ...props.styles,
        height,
        width,
        minWidth: props.styles.minWidth ?? DEFAULT_MIN_SIZES,
        minHeight: props.styles.minHeight ?? DEFAULT_MIN_SIZES,
      },
    };
  },

  dialogPosition: (props: MakeStyleOptions) => {
    if (!props.dialogPosition) return {};
    return { position: 'absolute', left: props.dialogPosition.left, top: props.dialogPosition.top };
  },
});

export const dialogWindowSelector = '.dp-dialog-paper';

/** Make sure dialog does not get outside of viewport when _resizing_ */
export const positionDialog = (options: PositionOptions, dialogMargin?: number): ControlPosition => {
  const margin = dialogMargin ?? DIALOG_MARGIN;
  // should always have a paper element, but
  const dialogElement: HTMLElement | null | undefined = options.paperElement?.querySelector(dialogWindowSelector);
  let x = 0;
  let y = 0;

  if (IS_TEST) {
    return { x, y };
  }

  if (!dialogElement) {
    return { x, y };
  }

  const offset = getOffset(dialogElement);
  const transformStyle = parseTransform(dialogElement);

  if (options.fixed) {
    // just put it where it's supposed to be:
    x = options.fixed.x;
    y = options.fixed.y;
    // TODO some boundary checking here, too?
    dialogElement.style.transform = `translate(${x}px, ${y}px)`;
    return { x, y };
  }

  const yAxis = transformStyle.y;
  const xAxis = transformStyle.x;

  if (!options.data) {
    return { x: xAxis, y: yAxis };
  }

  // center according to size
  const bottom = offset.top + options.data.size.height + margin;
  const right = offset.left + options.data.size.width + margin;
  const hasEnoughLeftSpace = offset.left > margin;
  const hasEnoughTopSpace = offset.top > margin;
  const hasEnoughRightSpace = right < options.windowWidth;
  const hasEnoughBottomSpace = bottom < options.windowHeight;
  const doTransform = !hasEnoughLeftSpace || !hasEnoughTopSpace || !hasEnoughRightSpace || !hasEnoughBottomSpace;
  if (doTransform) {
    x = hasEnoughLeftSpace
      ? hasEnoughRightSpace
        ? xAxis
        : xAxis - (right - options.windowWidth)
      : xAxis - offset.left + margin;
    y = hasEnoughTopSpace
      ? hasEnoughBottomSpace
        ? yAxis
        : yAxis - (bottom - options.windowHeight)
      : yAxis - offset.top + margin;
    dialogElement.style.transform = `translate(${x}px, ${y}px)`;
  }
  return { x, y };
};
